import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Box, Text } from "grommet";
import { useTranslation } from "react-i18next";
import { editorDeltaToMessage, messageToEditorHtml } from "./messageFormatting";
import ReactQuill, { Quill } from "react-quill";
import "./TagPickerButton.css";

const DEFAULT_TAGS = [
  "firstName",
  "lastName",
  "checkinDate",
  "checkinTime",
  "checkoutDate",
  "checkoutTime",
];

const TAG_LABELS = {
  chatLink: "Chat link",
  firstName: "First Name",
  lastName: "Last Name",
  hotel: "Hotel",
  hotelPhone: "Hotel Phone",
  checkinDate: "Check-in Date",
  checkinTime: "Check-in Time",
  checkoutDate: "Check-out Date",
  checkoutTime: "Check-out Time",
  roomsAndCodes: "Rooms & Codes",
  RoomsAndCodes: "Rooms & Codes",
  downloadAppButton: "Download App Button",
  mobileAppGuide: "Mobile App Guide",
  upsells: "Upsells",
  precheckinButton: "Pre-Checkin Button",
  stayDatesBox: "Stay Dates",
  magicLink: "Guest app",
  mobileAppLink: "Guest app",
};

// Register a custom Quill Embed blot so pills survive Quill's HTML processing
const Embed = Quill.import("blots/embed");

class TagPillBlot extends Embed {
  static create(value) {
    const node = super.create();
    node.setAttribute("data-tag", value);
    node.setAttribute("contenteditable", "false");
    node.textContent = TAG_LABELS[value] || value;
    Object.assign(node.style, {
      display: "inline-block",
      background: "#e0f7fa",
      border: "1px solid #4dd0e1",
      borderRadius: "12px",
      padding: "1px 8px",
      margin: "0 2px",
      fontSize: "13px",
      color: "#00838f",
      fontWeight: "500",
      cursor: "default",
      lineHeight: "1.4",
      userSelect: "all",
    });
    return node;
  }

  static value(node) {
    return node.getAttribute("data-tag");
  }
}

TagPillBlot.blotName = "tag-pill";
TagPillBlot.tagName = "span";
TagPillBlot.className = "ql-tag-pill";

Quill.register(TagPillBlot);
Quill.import("ui/icons").dynamicTag = '<span>Tag</span><span class="tag-picker-caret" aria-hidden="true"></span>';

// Convert {tagName} in HTML to pill blot markup that Quill recognises
function tagsToHtml(html) {
  if (!html) return html;
  return html.replace(/\{(\w+)\}/g, (match, tag) => {
    const label = TAG_LABELS[tag] || tag;
    return (
      '<span class="ql-tag-pill" data-tag="' +
      tag +
      '" contenteditable="false">' +
      label +
      "</span>"
    );
  });
}

// Convert pill blot markup back to {tagName} tokens for storage
function htmlToTags(html) {
  if (!html) return html;
  return html.replace(
    /<span[^>]*class="ql-tag-pill"[^>]*data-tag="(\w+)"[^>]*>[^<]*<\/span>/g,
    (match, tag) => "{" + tag + "}",
  );
}

export default function TagQuillEditor({
  value = "",
  onChange,
  onBlur,
  tags = DEFAULT_TAGS,
  style,
  valueFormat = "html",
}) {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [filter, setFilter] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const quillRef = useRef(null);
  const menuRef = useRef(null);
  const braceIndexRef = useRef(-1);
  const lastValueRef = useRef(value);

  const filteredTags = tags.filter((t) =>
    t.toLowerCase().startsWith(filter.toLowerCase()),
  );

  // Sync external value changes into the editor
  useEffect(() => {
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const sel = editor.getSelection();
    const html = tagsToHtml(valueFormat === "markdown" ? messageToEditorHtml(value) : value);
    editor.clipboard.dangerouslyPasteHTML(html);
    if (sel) {
      setTimeout(() => editor.setSelection(sel), 0);
    }
  }, [value, valueFormat]);

  const handleChange = useCallback(
    (html, delta, source, editor) => {
      const plain = valueFormat === "markdown" ? editorDeltaToMessage(editor.getContents()) : htmlToTags(html);
      if (valueFormat === "markdown" && plain === lastValueRef.current) return;
      lastValueRef.current = plain;
      onChange(plain);
    },
    [onChange, valueFormat],
  );

  const insertTag = useCallback((tag) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const braceIdx = braceIndexRef.current;
    const selection = editor.getSelection();
    const cursorPos = selection ? selection.index : editor.getLength();
    const deleteLen = cursorPos - braceIdx;
    editor.deleteText(braceIdx, deleteLen);
    editor.insertEmbed(braceIdx, "tag-pill", tag);
    setTimeout(() => {
      editor.setSelection(braceIdx + 1);
    }, 0);
    setShowMenu(false);
    setFilter("");
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (!showMenu) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, filteredTags.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" || e.key === "Tab") {
        if (filteredTags.length > 0) {
          e.preventDefault();
          insertTag(filteredTags[selectedIndex]);
        }
      } else if (e.key === "Escape") {
        setShowMenu(false);
        setFilter("");
      }
    },
    [showMenu, filteredTags, selectedIndex, insertTag],
  );

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const editorEl = editor.root;
    editorEl.addEventListener("keydown", handleKeyDown);
    return () => editorEl.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const handleTextChange = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const selection = editor.getSelection();
    if (!selection) {
      setShowMenu(false);
      return;
    }
    const cursorPos = selection.index;
    // Each pill occupies one Quill position, even though getText omits embeds.
    const text = editor.getContents(0, cursorPos).ops.map(op => typeof op.insert === "string" ? op.insert : "\ufffc").join("");
    const lastBrace = text.lastIndexOf("{");
    const lastCloseBrace = text.lastIndexOf("}");

    if (lastBrace > lastCloseBrace) {
      const partial = text.slice(lastBrace + 1);
      if (/^[a-zA-Z]*$/.test(partial)) {
        braceIndexRef.current = lastBrace;
        setFilter(partial);
        setSelectedIndex(0);

        const bounds = editor.getBounds(cursorPos);
        const editorRect = editor.root.getBoundingClientRect();
        const containerRect = editor.root.parentElement.getBoundingClientRect();
        setMenuPos({
          top: bounds.bottom + (editorRect.top - containerRect.top) + 4,
          left: Math.min(bounds.left, containerRect.width - 180),
        });
        setShowMenu(true);
        return;
      }
    }
    setShowMenu(false);
    setFilter("");
  }, []);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    editor.on("text-change", handleTextChange);
    editor.on("selection-change", handleTextChange);
    return () => {
      editor.off("text-change", handleTextChange);
      editor.off("selection-change", handleTextChange);
    };
  }, [handleTextChange]);

  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
        setFilter("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showMenu]);

  // ReactQuill tears down its DOM when a toolbar handler changes identity.
  // Keep handlers stable even when React refreshes memoized callbacks.
  const textChangeRef = useRef(handleTextChange);
  textChangeRef.current = handleTextChange;
  const [toolbarHandlers] = useState(() => ({
    dynamicTag: function () {
      const editor = this.quill;
      const selection = editor.getSelection(true);
      const index = selection ? selection.index : Math.max(0, editor.getLength() - 1);
      if (index === 0 || editor.getText(index - 1, 1) !== "{") {
        editor.insertText(index, "{", "user");
        editor.setSelection(index + 1, 0, "user");
      } else {
        editor.setSelection(index, 0, "user");
      }
      textChangeRef.current();
    },
  }));
  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: valueFormat === "markdown" ? [["bold", "italic", "link"], [{ list: "ordered" }, { list: "bullet" }], ["dynamicTag"]] : [
          ["bold", "italic", "underline"], ["link"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ align: "" }, { align: "center" }, { align: "right" }],
          ["clean"], ["dynamicTag"],
        ],
        handlers: toolbarHandlers,
      },
    }),
    [valueFormat, toolbarHandlers],
  );

  const quillFormats = useMemo(
    () => valueFormat === "markdown" ? ["bold", "italic", "link", "list", "tag-pill"] : ["bold", "italic", "underline", "link", "list", "align", "tag-pill"],
    [valueFormat],
  );

  useEffect(() => {
    const toolbar = quillRef.current?.getEditor()?.getModule("toolbar");
    const button = toolbar?.container.querySelector(".ql-dynamicTag");
    if (button) {
      button.type = "button";
      button.title = t("automation.insertDynamicValue");
      button.setAttribute("aria-label", t("automation.insertDynamicValue"));
    }
  }, [t]);

  // Set initial content on mount
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const html = tagsToHtml(valueFormat === "markdown" ? messageToEditorHtml(value) : value);
    editor.clipboard.dangerouslyPasteHTML(html);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box style={{ position: "relative" }}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        defaultValue=""
        onChange={handleChange}
        onBlur={() => {
          if (onBlur) onBlur(lastValueRef.current);
        }}
        modules={quillModules}
        formats={quillFormats}
        style={{ minHeight: 200, ...style }}
      />
      {showMenu && filteredTags.length > 0 && (
        <Box
          ref={menuRef}
          style={{
            position: "absolute",
            top: menuPos.top,
            left: menuPos.left,
            zIndex: 100,
          }}
          background="white"
          elevation="medium"
          round="xsmall"
          border={{ color: "light-4" }}
          width={{ min: "180px" }}
          overflow="auto"
          pad={{ vertical: "xxsmall" }}
        >
          {filteredTags.map((tag, i) => (
            <Box
              key={tag}
              pad={{ horizontal: "small", vertical: "xxsmall" }}
              background={i === selectedIndex ? "light-2" : "white"}
              hoverIndicator="light-2"
              onMouseDown={(e) => {
                e.preventDefault();
                insertTag(tag);
              }}
              style={{ cursor: "pointer" }}
            >
              <Text size="small">
                <span
                  style={{
                    display: "inline-block",
                    background: "#e0f7fa",
                    border: "1px solid #4dd0e1",
                    borderRadius: 12,
                    padding: "1px 8px",
                    color: "#00838f",
                    fontWeight: 500,
                    fontSize: 13,
                  }}
                >
                  {TAG_LABELS[tag] || tag}
                </span>
              </Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
